import { Row } from 'react-table';
import memoize from 'lodash/memoize';
import { Filter } from '../DataGridFilterMenu/DataGridFilterTypes';
import { startCase } from 'lodash';

export interface BooleanOptionFilter {
  [key: string]: boolean;
}

export const booleanEnumFilterOptions = (optionFilters: BooleanOptionFilter) => {
  const filters = Object.keys(optionFilters).map((filter) => startCase(filter));
  return filters;
};

export type BooleanFilterFunction = (
  cellValue: boolean,
  inputValue: string,
  optionsToFilter: BooleanOptionFilter,
) => boolean;

const isFilter: BooleanFilterFunction = (
  cellValue: boolean,
  inputValue: string,
  optionsToFilter: BooleanOptionFilter,
) => {
  return optionsToFilter[inputValue.toLocaleLowerCase()] === cellValue;
};

const isNotFilter: BooleanFilterFunction = (
  cellValue: boolean,
  inputValue: string,
  optionsToFilter: BooleanOptionFilter,
) => {
  return optionsToFilter[inputValue.toLocaleLowerCase()] !== cellValue;
};

const memoizeResolver = (...args: any[]) => args.join('_'); // eslint-disable-line
const filtersByOperator: Record<string, BooleanFilterFunction> = {
  is: memoize(isFilter, memoizeResolver),
  isNot: memoize(isNotFilter, memoizeResolver),
};

function runAllFiltersOnRow<T extends Record<string, unknown>>(
  row: Row<T>,
  filters: Filter[],
  optionsToFilter: BooleanOptionFilter,
) {
  // Find a filter that causes the row to be removed
  return !filters.find((filter) => {
    const filterFunc = filtersByOperator[filter.operator as string];
    return !filterFunc(row.values[filter.columnId as string] as boolean, filter.value as string, optionsToFilter);
  });
}

export function dataGridBooleanFilterFunc<T extends Record<string, unknown> = Record<string, unknown>>(
  rows: Row<T>[],
  columnIds: string[],
  filterValue: Filter[],
  optionsToFilter: BooleanOptionFilter,
): Row<T>[] {
  // Get valid filters
  const filters = filterValue.filter((filter: Filter) => {
    return !!filter?.operator && !!filter?.columnId && !!filter.value && !!filtersByOperator[filter.operator];
  });

  return rows.filter((row) => {
    return runAllFiltersOnRow(row, filters, optionsToFilter);
  });
}

export default dataGridBooleanFilterFunc;
